import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import { Minus, Plus, X } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";
import useApiMutation from "../../hooks/useApiMutation";
import { storeAddonsToCart } from "../../utilities/service";

const AddonDialog = forwardRef(
  (
    {
      data,
      addonItems,
      setAddonItems,
      showSkip,
      setShowSkip,
      refetch,
      skipHandler,
      cartHasStreamProduct,
      cartHasNormalProduct,
    },
    ref,
  ) => {
    const { user, setUser } = useAuth();

    const { mutate, isPending } = useApiMutation({
      mutationFn: (param) => storeAddonsToCart(param),
      options: {
        onSuccess: (data) => {
          if (!data?.error) {
            setUser((prev) => ({
              ...prev,
              cartCount: data?.data?.addon_count,
            }));
            refetch();
            setOpen(false);
            setShowSkip(true);
          } else {
            toast.error(data?.message);
          }
        },
        onError: (error) => {
          toast.error(error?.message);
        },
      },
    });

    const [open, setOpen] = useState(false);
    const normalAddons = data?.filter((item) => item?.stream_engine === 0);
    const streamAddons = data?.filter((item) => item?.stream_engine === 1);

    const defaultTab =
      cartHasStreamProduct && !cartHasNormalProduct ? "stream" : "normal";

    const [activeTab, setActiveTab] = useState(defaultTab);

    // ✅ Add this — re-sync when cart contents change
    useEffect(() => {
      const tab =
        cartHasStreamProduct && !cartHasNormalProduct ? "stream" : "normal";
      setActiveTab(tab);
    }, [cartHasStreamProduct, cartHasNormalProduct]);

    const renderData = activeTab === "stream" ? streamAddons : normalAddons;

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    useImperativeHandle(ref, () => ({
      openDialog: () => {
        setOpen(true);
        setShowSkip(true);
      },
      closeDialog: () => {
        setOpen(false);
        setShowSkip(true);
      },
    }));

    const currentAddonTotal = addonItems?.reduce(
      (sum, addon) => sum + +addon?.price * +addon?.quantity,
      0,
    );

    const addItemsHandler = () => {
      if (addonItems?.length > 0) {
        const param = {
          customer_id: user?.customer_id,
          selected_Addon: addonItems,
        };
        mutate(param);
      }
    };

    const handleAddonPlus = (addonItem, isIncrement = true) => {
      const index = addonItems?.findIndex(
        (item) => item?.addon_id === addonItem?.addon_id,
      );
      if (index !== -1) {
        const newArray = [...addonItems];
        const currentItem = newArray[index];
        if (isIncrement) {
          newArray[index] = {
            ...currentItem,
            quantity: (currentItem.quantity || 1) + 1,
          };
        } else {
          const newQuantity = (currentItem.quantity || 1) - 1;
          if (newQuantity <= 0) {
            newArray.splice(index, 1);
          } else {
            newArray[index] = { ...currentItem, quantity: newQuantity };
          }
        }
        setAddonItems(newArray);
      } else {
        if (isIncrement) {
          const newItem = {
            ...addonItem,
            quantity: Math.max(1, addonItem.quantity || 1),
          };
          setAddonItems((prevArray) => [...prevArray, newItem]);
        }
      }
      setShowSkip(false);
    };

    const showTabs =
      cartHasNormalProduct && cartHasStreamProduct && streamAddons?.length > 0;
    const dialogTitle =
      cartHasNormalProduct && cartHasStreamProduct
        ? "Add something extra to your order"
        : activeTab === "stream"
          ? "Add a treat to your order"
          : "Make your gift more special";
    return (
      <Dialog
        fullScreen={fullScreen}
        open={open}
        maxWidth="lg"
        onClose={() => setOpen(false)}
        sx={dialogStyles}
      >
        {/* Header */}
        <Box sx={headerContainer}>
          <DialogTitle
            sx={{ ...titleStyles, fontSize: { xs: "18px", sm: "28px" } }}
          >
            {dialogTitle}
          </DialogTitle>
          <IconButton sx={closeButtonStyles} onClick={() => setOpen(false)}>
            <X size={24} />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1, mx: 3 }} />

        {/* Content */}
        <DialogContent>
          {/* ── Toggleable / Dynamic Tabs ── */}
          {showTabs && (
            <Box sx={tabBarWrapper}>
              {cartHasNormalProduct && (
                <Box
                  component="button"
                  onClick={() => setActiveTab("normal")}
                  sx={
                    activeTab === "normal" ? activeTabStyle : inactiveTabStyle
                  }
                >
                  Make it special
                </Box>
              )}
              {cartHasStreamProduct && streamAddons?.length > 0 && (
                <Box
                  component="button"
                  onClick={() => setActiveTab("stream")}
                  sx={
                    activeTab === "stream" ? activeTabStyle : inactiveTabStyle
                  }
                >
                  Add a treat
                </Box>
              )}
            </Box>
          )}

          {/* Items Grid */}
          <Grid
            container
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            {renderData?.map((item, index) => {
              const isItemInCart = addonItems?.some(
                (i) => i?.addon_id === item.addon_id,
              );
              return (
                <AddonItem
                  qty={
                    addonItems?.find((elt) => elt?.addon_id == item?.addon_id)
                      ?.quantity
                  }
                  key={index}
                  item={item}
                  isItemInCart={isItemInCart}
                  handleAddonPlus={handleAddonPlus}
                />
              );
            })}
          </Grid>

          {/* Price Details & Actions */}
          <Box sx={priceDetailsContainer}>
            <Typography
              sx={{
                ...bottomLabel,
                color: "var(--neutral-black)",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              Price Details
            </Typography>
            <Box sx={priceDetailsBox}>
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  display: "flex",
                  gap: 3,
                }}
              >
                <Typography sx={bottomLabel}>
                  Addon's :{" "}
                  <span style={bottomValue}>{addonItems?.length}</span>
                </Typography>
                <Typography sx={bottomLabel}>
                  Total :{" "}
                  <span style={bottomValue}>
                    ₹ {currentAddonTotal?.toFixed(2) || 0}
                  </span>
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={addItemsButton}
                onClick={addonItems?.length > 0 ? addItemsHandler : skipHandler}
              >
                {addonItems?.length > 0 ? "Add Items" : "Skip"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  },
);

const AddonItem = ({ item, isItemInCart, handleAddonPlus, qty }) => {
  return (
    <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }} sx={addonItemContainer}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 0.5, sm: 1 },
        }}
      >
        <img src={item?.image} alt={item?.product_name} style={addonImage} />
        <Typography sx={addonTitle}>{item?.product_name}</Typography>
        <Typography sx={addonPrice}>₹{item?.price}</Typography>
      </Box>
      {isItemInCart ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-evenly",
            alignItems: "center",
            bgcolor: "#f9f8f8",
          }}
        >
          <IconButton
            sx={{
              borderRadius: "8px",
              "&.MuiIconButton-root": {
                borderRadius: "8px",
                backgroundColor: "transparent",
              },
              "&.MuiIconButton-root:hover": {
                borderRadius: "8px",
                backgroundColor: "transparent",
              },
            }}
            aria-label="decrease"
            onClick={() => handleAddonPlus({ ...item }, false)}
          >
            <Minus size={18} />
          </IconButton>
          <Typography sx={addonTitle}>{qty}</Typography>
          <IconButton
            sx={{
              borderRadius: "8px",
              "&.MuiIconButton-root": { borderRadius: "8px" },
              "&.MuiIconButton-root:hover": {
                borderRadius: "8px",
                backgroundColor: "none",
              },
            }}
            aria-label="increase"
            onClick={() => handleAddonPlus({ ...item }, true)}
          >
            <Plus size={18} />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          onClick={() => handleAddonPlus({ ...item, quantity: 1 })}
          sx={addButton}
        >
          Add
        </Button>
      )}
    </Grid>
  );
};

// ── Styles ──────────────────────────────────────────────

const dialogStyles = {
  "& .MuiPaper-root": {
    backgroundColor: "var(--neutral-100)",
    borderRadius: "8px",
  },
};

const headerContainer = {
  display: "flex",
  flexDirection: "row",
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
};

const titleStyles = {
  textAlign: "center",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "150%",
  color: "var(--neutral-black)",
};

const closeButtonStyles = {
  color: "var(--neutral-black)",
  position: "absolute",
  right: "24px",
};

// ── Tab styles ──────────────────────────────────────────

const tabBarWrapper = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  borderBottom: "1px solid var(--primary-red-base)",
  mb: 1,
  gap: "2px",
};

const baseTabStyle = {
  padding: "8px 20px",
  fontSize: "15px",
  fontWeight: 400,
  cursor: "pointer",
  border: "none",
  background: "transparent",
  borderRadius: "4px 4px 0 0",
  position: "relative",
  bottom: "-0.5px",
  fontFamily: "inherit",
  transition: "color 0.15s",
};

const activeTabStyle = {
  ...baseTabStyle,
  color: "var(--primary-red-800)",
  background: "#ffffff",
  border: "2px solid var(--primary-red-base)",
  borderBottom: "0px solid var(--primary-red-base)",
  zIndex: 1,
  fontWeight: 600,
};

const inactiveTabStyle = {
  ...baseTabStyle,
  color: "var(--neutral-black)",
  borderBottom: "0px solid var(--primary-red-base)",
};

// ── Bottom price area ───────────────────────────────────

const priceDetailsContainer = {
  backgroundColor: "var(--neutral-white)",
  p: 3,
  mt: 3,
  height: "96px",
  borderRadius: "8px",
  alignItems: "center",
  justifyContent: "space-between",
  display: "flex",
};

const priceDetailsBox = {
  py: 3,
  height: "96px",
  borderRadius: "8px",
  alignItems: "center",
  justifyContent: "space-between",
  display: "flex",
  width: { xs: "100%", sm: "75%" },
  flexDirection: { xs: "column", sm: "row" },
  gap: { xs: 2, sm: 1 },
};

const bottomValue = {
  fontSize: 16,
  fontWeight: 600,
  color: "var(--neutral-black)",
  paddingLeft: "12px",
};

const bottomLabel = {
  fontSize: 16,
  fontWeight: 500,
  color: "var(--neutral-700)",
  lineHeight: "150%",
};

const addonItemContainer = {
  border: "1px solid var(--neutral-300)",
  borderRadius: "8px",
  width: { md: "160px" },
  background: "var(--neutral-white)",
  p: 1,
  gap: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const addonImage = {
  borderRadius: "4px",
  backgroundColor: "#eef0ff",
  width: "100%",
  height: "100px",
  objectFit: "contain",
  objectPosition: "center",
};

const addonTitle = {
  fontWeight: 600,
  fontSize: { xs: "11px", sm: "12px" },
  color: "var(--neutral-800)",
  lineHeight: "167%",
};

const addonPrice = {
  fontWeight: 600,
  fontSize: { xs: "13px", sm: "14px" },
  color: "var(--neutral-black)",
  lineHeight: "157%",
};

const addButton = {
  mt: 1,
  fontSize: "14px",
  fontWeight: 600,
  width: "100%",
  borderRadius: "4px",
  height: "32px",
  border: "1px solid var(--primary-red-base)",
};

const addItemsButton = {
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  height: "48px",
  px: 12,
};

export default AddonDialog;
